class AnnouncementBar extends HTMLElement {
  constructor() {
    super();

    this.config = {
      autorotate: this.dataset.autorotate === 'true',
      autorotateSpeed: parseInt(this.dataset.autorotateSpeed) * 1000,
      moveTime: parseFloat(this.dataset.speed), // 100px going to move for
      space: 100, // 100px
    };

    this.resizeFrame = 0;
    this.carouselInitTimer = 0;
    this.marqueeInitTimer = 0;

    this.onResizeHandler = () => {
      if (this.resizeFrame) return;
      this.resizeFrame = requestAnimationFrame(() => {
        this.resizeFrame = 0;
        if (this.isConnected) this.marqueeAnimation();
      });
    };

    this.onMatchSmallHandler = () => {
      this.unload();
      this.initOnMobile();
    };

    this.onUnmatchSmallHandler = () => {
      this.unload();
      this.initOnDesktop();
    };

    if (theme.config.mqlSmall) {
      this.initOnMobile();
    }
    else {
      this.initOnDesktop();
    }

    document.addEventListener('matchSmall', this.onMatchSmallHandler);
    document.addEventListener('unmatchSmall', this.onUnmatchSmallHandler);
  }

  disconnectedCallback() {
    this.unload();
    document.removeEventListener('matchSmall', this.onMatchSmallHandler);
    document.removeEventListener('unmatchSmall', this.onUnmatchSmallHandler);
  }

  initOnDesktop() {
    switch(this.dataset.layout) {
      case 'carousel':
        this.initCarousel();
        break;

      case 'marquee':
        this.initMarquee();
        break;

      default:
        this.initMarqueeOptimized();
    }
  }

  initOnMobile() {
    switch(this.dataset.mobileLayout) {
      case 'carousel':
        this.initCarousel();
        break;

      case 'marquee':
        this.initMarquee();
        break;
    }
  }

  unload() {
    if (this.carouselInitTimer) {
      clearTimeout(this.carouselInitTimer);
      this.carouselInitTimer = 0;
    }
    if (this.marqueeInitTimer) {
      clearTimeout(this.marqueeInitTimer);
      this.marqueeInitTimer = 0;
    }

    window.removeEventListener('resize', this.onResizeHandler);
    if (this.resizeFrame) {
      cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = 0;
    }

    if (this.flickity && typeof this.flickity.destroy === 'function') {
      this.flickity.destroy();
    }
    this.flickity = null;
    this.removeNextPrevListeners();

    this.classList.remove('marquee', 'marquee--paused');
    this.querySelectorAll('.announcement-slider[aria-hidden]').forEach((slider) => {
      slider.remove();
    });
    this.style.removeProperty('--duration');
    this.isAnimated = false;

    if (this.observer) {
      if (typeof this.observer.disconnect === 'function') {
        this.observer.disconnect();
      }
      else if (typeof this.observer.unobserve === 'function') {
        this.observer.unobserve(this);
      }
      this.observer = null;
    }
  }

  initCarousel() {
    if (this.dataset.blockCount == 1) {
      return;
    }

    const slider = this.querySelector('.announcement-slider');
    this.carouselInitTimer = setTimeout(() => {
      this.carouselInitTimer = 0;
      if (!this.isConnected || !slider) return;
      this.flickity = new Flickity(slider, {
        accessibility: false,
        rightToLeft: theme.config.rtl,
        prevNextButtons: false,
        pageDots: false,
        wrapAround: true,
        setGallerySize: false,
        autoPlay: this.config.autorotate ? this.config.autorotateSpeed : false,
      });
    });

    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    this.onNextPrevClick = this.handleNextPrevClick.bind(this);
    this.prevButton && this.prevButton.addEventListener('click', this.onNextPrevClick);
    this.nextButton && this.nextButton.addEventListener('click', this.onNextPrevClick);
  }

  removeNextPrevListeners() {
    this.prevButton && this.prevButton.removeEventListener('click', this.onNextPrevClick);
    this.nextButton && this.nextButton.removeEventListener('click', this.onNextPrevClick);
    this.prevButton = null;
    this.nextButton = null;
    this.onNextPrevClick = null;
  }

  handleNextPrevClick(event) {
    event.preventDefault();
    const target = event.currentTarget;
    
    if (target.name === 'next') {
      this.flickity && this.flickity.next();
    }
    else if (target.name === 'previous') {
      this.flickity && this.flickity.previous();
    }
  }

  initMarqueeOptimized() {
    this.marqueeInitTimer = setTimeout(() => {
      this.marqueeInitTimer = 0;
      if (this.isConnected) this.marqueeAnimation();
    });

    // pause when out of view
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.isAnimated && this.classList.remove('marquee--paused');
        }
        else {
          this.isAnimated && this.classList.add('marquee--paused');
        }
      });
    }, {rootMargin: '0px 0px 50px 0px'});

    this.observer.observe(this);
    window.addEventListener('resize', this.onResizeHandler, { passive: true });
  }

  marqueeAnimation() {
    const slider = this.querySelector('.announcement-slider');
    if (!slider) return;

    if (slider.scrollWidth > this.clientWidth) {
      if (!this.isAnimated) {
        for (let index = 0; index < 2; index++) {
          const clone = slider.cloneNode(true);
          clone.setAttribute('aria-hidden', true);
          this.appendChild(clone);
        }
        
        this.classList.add('marquee');
        this.isAnimated = true;
      }
      this.style.setProperty('--duration', `${(slider.scrollWidth / this.config.space) * this.config.moveTime}s`);
    }
    else if (this.isAnimated) {
      this.querySelectorAll('.announcement-slider[aria-hidden]').forEach((clone) => clone.remove());
      this.classList.remove('marquee', 'marquee--paused');
      this.style.removeProperty('--duration');
      this.isAnimated = false;
    }
  }

  initMarquee() {
    this.marqueeInitTimer = setTimeout(() => {
      this.marqueeInitTimer = 0;
      if (!this.isConnected) return;
      const slider = this.querySelector('.announcement-slider');
      if (!slider) return;

      for (let index = 0; index < 10; index++) {
        const clone = slider.cloneNode(true);
        clone.setAttribute('aria-hidden', true);
        this.appendChild(clone);
      }
  
      this.classList.add('marquee');
      this.style.setProperty('--duration', `${(slider.clientWidth / this.config.space) * this.config.moveTime}s`);
    });

    // pause when out of view
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.classList.remove('marquee--paused');
        }
        else {
          this.classList.add('marquee--paused');
        }
      });
    }, {rootMargin: '0px 0px 50px 0px'});

    this.observer.observe(this);
  }
}
customElements.define('announcement-bar', AnnouncementBar);
